// Per-list color tag. The user picks one from the tile's ⋯ menu, and
// the chosen id is stored as `list.color` on the Firestore list doc.
// Missing / null `color` = no accent (default appearance).
//
// Why a curated palette and not arbitrary colors:
//   1. Cohesion. listicity's brand palette is warm+monospace; we don't
//      want fluorescent rainbow stickers next to that. These swatches
//      are all picked to read against both the cream light-mode and
//      the deep warm dark-mode surfaces without competing with the
//      burnt-orange brand accent.
//   2. Stable contracts. With a fixed id set, the renderer in
//      ListPanel / FocusedPanel / (later) Sidebar can do a quick map
//      lookup; we never have to validate arbitrary hex values from
//      user input.
//   3. Theme migration. If we ever tune the palette per-theme, the id
//      stays the same on the doc — only the resolved value changes.
//      Walter 2026-05-24: per Tahmid's tester feedback ("no visual
//      differentiation between list types"), this is the minimum
//      surface that lets users visually tag lists without inventing
//      a "list type" concept.

export const LIST_COLORS = [
  // `text` = the variant used when the colour is rendered as TEXT (board/list
  // names in the sidebar, tile titles). More saturated + darker than the
  // swatch `value` so the coloured names read clearly on the cream surfaces
  // (Walter 2026-07-03: "could be a little more saturated — true on phone and
  // desktop"). The swatch/picker + washes still use the softer `value`.
  { id: 'orange', label: 'orange', value: '#e8622c', text: '#d14a0e' },
  { id: 'amber',  label: 'amber',  value: '#d9b341', text: '#9c7404' },
  { id: 'green',  label: 'green',  value: '#5fa055', text: '#3c8f33' },
  { id: 'blue',   label: 'blue',   value: '#4f80c4', text: '#2e6cc7' },
  { id: 'purple', label: 'purple', value: '#9168b8', text: '#7f47c4' },
  { id: 'pink',   label: 'pink',   value: '#c46090', text: '#c2397e' },
  { id: 'gray',   label: 'gray',   value: '#7a7566', text: '#5d574a' },
];

// Resolve a list.color id to a CSS color string. Returns null for
// missing / unknown ids so the renderer can fall through to the
// default (no accent border) cleanly.
export function getListColorValue(colorId) {
  if (!colorId) return null;
  const entry = LIST_COLORS.find(c => c.id === colorId);
  return entry ? entry.value : null;
}

// The colour to use when painting the board/list NAME text in that colour.
// Falls back to `value` for colours that are already legible as text. Returns
// null for no-colour so callers fall through to their default text colour.
export function getListColorTextValue(colorId) {
  if (!colorId) return null;
  const entry = LIST_COLORS.find(c => c.id === colorId);
  if (!entry) return null;
  return entry.text || entry.value;
}

// Walter 2026-06-13: a low-opacity rgba tint of the swatch, for washing the
// tile HEADER band (the 3px left edge read as too timid). Returned as rgba
// so it can be layered over the header's --surface2 via a doubled
// linear-gradient — that blend stays theme-aware (pale over the cream light
// header, muted over the dark header) without needing color-mix support.
// Default alpha is deliberately soft so titles stay readable and shared
// boards don't turn into a fruit salad.
export function getListColorTint(colorId, alpha = 0.18) {
  const v = getListColorValue(colorId);
  if (!v || v[0] !== '#' || v.length < 7) return null;
  const r = parseInt(v.slice(1, 3), 16);
  const g = parseInt(v.slice(3, 5), 16);
  const b = parseInt(v.slice(5, 7), 16);
  if ([r, g, b].some(Number.isNaN)) return null;
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

// Convenience: a ready-to-use `background` value that washes the swatch
// tint over a base color (default the header's --surface2). null when no
// color is set, so callers fall through to their plain background.
export function getListColorHeaderBg(colorId, base = 'var(--surface2)', alpha = 0.18) {
  const tint = getListColorTint(colorId, alpha);
  if (!tint) return null;
  return `linear-gradient(${tint}, ${tint}), ${base}`;
}

// Walter 2026-08-25: the BODY wash. He'd called the original 3px left edge
// "too timid", and after seeing the header strip: "let's try the wash on the
// tile colors. It looks good but should be subtle." So the whole tile takes
// the colour — faint enough that note text keeps its contrast and a shared
// board doesn't turn into a fruit salad. The header stays the stronger band,
// so the tile still has a top edge.
//
// The alpha is deliberately LOW. It took three passes on Walter's real board
// to land: 0.06 read "quite heavy on a bigger canvas", 0.04 lost the signal
// ("actually back to .06"), and then at 0.06 the pink tiles made the cream
// chrome read GREEN — which sent us to neutralise --surface, which came back
// "lifeless" (see index.css). So the chrome stays warm and the WASH is the
// dial: 0.04. Judge any future change on a FULL board, never on a swatch
// sheet — samples make a wash look far more restrained than it reads across a
// canvas of tiles, which is what started this whole loop.
export function getListColorBodyBg(colorId, base = 'var(--surface-note)', alpha = 0.04) {
  const tint = getListColorTint(colorId, alpha);
  if (!tint) return null;
  return `linear-gradient(${tint}, ${tint}), ${base}`;
}
